import React, { useState } from 'react';
import { 
  UserPlusIcon, 
  ShieldCheckIcon, 
  DocumentTextIcon, 
  ArrowPathIcon,
  CheckCircleIcon,
  EnvelopeIcon,
  IdentificationIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

const EmployeeSetup = () => {
  // 1. STATE MANAGEMENT (Configuration State)
  const [config, setConfig] = useState({
    autoGenEmpId: true,
    welcomeEmail: true,
    probationPeriod: '6 Months',
    defaultRole: 'Associate',
    autoManager: false,
    mfaEnforced: true,
    attendancePolicy: 'Standard Flexi',
    leavePolicy: 'General (CL/PL)',
    workingHours: '09:00 - 18:00'
  });

  const [isSaving, setIsSaving] = useState(false);

  // 2. HANDLERS
  const handleToggle = (field) => {
    setConfig(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSelect = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      console.log("Global Employee Configuration Saved:", config);
      setIsSaving(false);
      alert("System configuration updated successfully.");
    }, 800);
  };

  const handleReset = () => {
    if (window.confirm("Reset all configurations to system defaults?")) {
      console.log("Resetting to factory defaults...");
    }
  };

  // Reusable Toggle Component
  const Toggle = ({ enabled, onClick }) => (
    <button
      onClick={onClick}
      className={`relative inline-flex h-5 w-10 items-center rounded-full transition-colors focus:outline-none ${
        enabled ? 'bg-indigo-600' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-5.5' : 'translate-x-1'
        }`}
      />
    </button>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-roboto text-slate-800">
      
      {/* 1. HEADER */}
      <header className="max-w-4xl mx-auto flex justify-between items-center mb-8">
        <div>
          <h1 className="font-poppins text-2xl font-bold text-slate-900 tracking-tight">Employee Setup</h1>
          <p className="text-xs text-slate-500 font-medium">Configure global onboarding, access & system defaults</p>
        </div>
        <div className="flex gap-2">
          <button 
            onClick={handleReset}
            className="px-3 py-1.5 text-xs font-bold text-slate-600 border border-slate-300 rounded hover:bg-slate-100 transition-colors"
          >
            Reset to Defaults
          </button>
          <button 
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-1.5 text-xs font-bold bg-indigo-600 text-white rounded hover:bg-indigo-700 transition-all shadow-sm flex items-center gap-2"
          >
            {isSaving ? <ArrowPathIcon className="w-3.5 h-3.5 animate-spin" /> : <CheckCircleIcon className="w-3.5 h-3.5" />}
            {isSaving ? 'Saving...' : 'Save Configuration'}
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6 pb-20">
        
        {/* 2. ONBOARDING RULES */}
        <section className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <UserPlusIcon className="w-4 h-4 text-indigo-600" />
            <h2 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Onboarding Rules</h2>
          </div>
          <div className="p-4 space-y-5">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-bold text-slate-700">Auto-generate Employee ID</p>
                <p className="text-[10px] text-slate-400 italic">Sequential ID based on prefix</p>
              </div>
              <Toggle enabled={config.autoGenEmpId} onClick={() => handleToggle('autoGenEmpId')} />
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-bold text-slate-700">Welcome Email Trigger</p>
                <p className="text-[10px] text-slate-400 italic">Sent automatically on hire date</p>
              </div>
              <Toggle enabled={config.welcomeEmail} onClick={() => handleToggle('welcomeEmail')} />
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Default Probation Period</label>
              <select 
                value={config.probationPeriod}
                onChange={(e) => handleSelect('probationPeriod', e.target.value)}
                className="w-full text-xs font-bold border-slate-200 rounded p-1.5 bg-white outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option>None</option>
                <option>3 Months</option>
                <option>6 Months</option>
                <option>1 Year</option>
              </select>
            </div>
          </div>
        </section>

        {/* 3. ACCESS DEFAULTS */}
        <section className="bg-white border border-slate-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <ShieldCheckIcon className="w-4 h-4 text-emerald-600" />
            <h2 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Access Defaults</h2>
          </div>
          <div className="p-4 space-y-5">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Default Role Assignment</label>
              <select 
                value={config.defaultRole}
                onChange={(e) => handleSelect('defaultRole', e.target.value)}
                className="w-full text-xs font-bold border-slate-200 rounded p-1.5 bg-white outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option>Associate</option>
                <option>Contractor</option>
                <option>Lead</option>
              </select>
            </div>
            <div className="flex justify-between items-center">
              <div>
                <p className="text-xs font-bold text-slate-700">Manager Auto-mapping</p>
                <p className="text-[10px] text-slate-400 italic">Map based on department leads</p>
              </div>
              <Toggle enabled={config.autoManager} onClick={() => handleToggle('autoManager')} />
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-50">
              <div>
                <p className="text-xs font-bold text-slate-700 text-indigo-600 flex items-center gap-1">
                  MFA Enforcement
                </p>
                <p className="text-[10px] text-slate-400 italic">Mandatory 2FA for all new users</p>
              </div>
              <Toggle enabled={config.mfaEnforced} onClick={() => handleToggle('mfaEnforced')} />
            </div>
          </div>
        </section>

        {/* 4. POLICY DEFAULTS */}
        <section className="bg-white border border-slate-200 rounded-lg shadow-sm md:col-span-2">
          <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
            <DocumentTextIcon className="w-4 h-4 text-amber-600" />
            <h2 className="text-[11px] font-bold text-slate-700 uppercase tracking-wider">Policy & Working Hour Defaults</h2>
          </div>
          <div className="p-4 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Attendance Policy</label>
              <select 
                value={config.attendancePolicy}
                onChange={(e) => handleSelect('attendancePolicy', e.target.value)}
                className="w-full text-xs font-bold border-slate-200 rounded p-1.5 bg-white outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option>Standard Flexi</option>
                <option>Strict 9-to-5</option>
                <option>Remote (Global)</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1">Leave Policy</label>
              <select 
                value={config.leavePolicy}
                onChange={(e) => handleSelect('leavePolicy', e.target.value)}
                className="w-full text-xs font-bold border-slate-200 rounded p-1.5 bg-white outline-none focus:ring-1 focus:ring-indigo-500"
              >
                <option>General (CL/PL)</option>
                <option>Unlimited PTO</option>
                <option>Start-up Tier</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] font-bold text-slate-400 uppercase block mb-1 text-slate-400">Default Working Hours</label>
              <div className="flex items-center gap-2 bg-slate-50 p-1.5 border border-slate-200 rounded">
                <ClockIcon className="w-4 h-4 text-slate-400" />
                <span className="text-xs font-bold text-slate-600">{config.workingHours}</span>
              </div>
            </div>
          </div>
        </section>

      </div>

      {/* SYSTEM STATUS FOOTER */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 px-6 py-2 flex justify-between items-center shadow-[0_-2px_10px_rgba(0,0,0,0.05)]">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-widest">System Sync Active</span>
        </div>
        <p className="text-[10px] text-slate-400 italic">All changes are logged in the Admin Audit Trail.</p>
      </footer>
    </div>
  );
};

export default EmployeeSetup;